import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import store from './redux/store';
import App from './Components/landingPageComponent/Landing page';
import Lang from './Components/selectedLanguageComponent/selectedLang';
import Question from './Components/selectedSurveyComponent/selectedSurvey';
import Feedback from './Components/feedbackComponent/Feedback';
import ProtectedRoute from './Components/protectedRouteComponent/protectedRouteComponent';
import Spinner from './Components/spinnerComponent/spinnerComponent';
import ErrorPage from './Components/errorPageComponent/errorPageComponent';
import { setSelectedLocation, clearSelectedLocation } from './redux/actions/experienceAction';
import { setSelectedAnswers } from './redux/actions/selctedSurveyAction';
import locationMapping from './Components/serviceComponents/itExperienceComponent/itExperienceLocations';

// --- AuthHandler ---
// Extracts token-related query parameters and stores them in localStorage.
const AuthHandler = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    // Clear any stored location if not on legacy route or /lang.
    if (
      !location.pathname.startsWith("/it-ux-survey/itcenter") &&
      location.pathname !== "/lang"
    ) {
      sessionStorage.removeItem('selectedLocation');
      dispatch(clearSelectedLocation());
    }

    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    const refreshToken = queryParams.get('refresh_token');

    if (token && refreshToken && !localStorage.getItem('authToken')) {
      localStorage.setItem('authToken', token);
      localStorage.setItem('refresh_token', refreshToken);
      localStorage.setItem('isAuthenticated', 'true');

      queryParams.delete('token');
      queryParams.delete('refresh_token');
      const newSearch = queryParams.toString();
      const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
      navigate(newUrl, { replace: true });
    }
  }, [location, navigate, dispatch]);

  return children;
};

// --- RedirectToLogin ---
// Clears any stored location and redirects based on authentication status.
const RedirectToLogin = () => {
  sessionStorage.removeItem("selectedLocation");
  const isAuthenticated = localStorage.getItem('isAuthenticated');
  if (!isAuthenticated) {
    window.location.href = '/login';
  } else {
    window.location.href = '/itux-happiness-survey';
  }
  return null;
};

// --- HandleOldRoutes ---
// Processes legacy routes (starting with /it-ux-survey/itcenter) and dispatches the selected location.
const HandleOldRoutes = () => {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const handleRoute = () => {
      const currentPath = location.pathname;
      const queryParams = new URLSearchParams(location.search);

      if (currentPath.startsWith("/it-ux-survey/itcenter")) {
        const userLocation = queryParams.get("location");
        dispatch(setSelectedAnswers(["IT Experience Center"]));
        if (userLocation) {
          console.log("User Location:", userLocation);
          const mappedLocation = locationMapping[userLocation.toLowerCase()];
          if (mappedLocation) {
            console.log("Mapped Location:", mappedLocation);
            dispatch(setSelectedLocation(mappedLocation));
            navigate("/lang", { replace: true });
          } else {
            console.error(`No mapping found for location: ${userLocation}`);
            dispatch(setSelectedLocation("unknown"));
            navigate("/error-page", { replace: true });
          }
        } else {
          console.error("Missing 'location' query parameter.");
          dispatch(setSelectedLocation("unknown"));
          navigate("/error-page", { replace: true });
        }
      } else {
        console.warn("Unrecognized route. Redirecting to default.");
        navigate("/itux-happiness-survey", { replace: true });
      }
      setLoading(false);
    };

    const timer = setTimeout(handleRoute, 500);
    return () => clearTimeout(timer);
  }, [location, navigate, dispatch]);

  if (loading) {
    return <Spinner />;
  }
  return null;
};

// --- NotFoundRedirect ---
// Catch-all route. If not authenticated, immediately redirect to /login.
// If authenticated, render the ErrorPage component.
const NotFoundRedirect = () => {
  const isAuthenticated = localStorage.getItem('isAuthenticated');
  if (!isAuthenticated) {
    window.location.href = '/login';
    return null;
  }
  return <ErrorPage />;
};


// --- Check for expired tokens on app load ---
// If user returns after browser close with expired tokens, clean them up.
const checkTokenExpiry = () => {
  const token = localStorage.getItem("authToken");
  if (!token) return;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload.exp * 1000 < Date.now()) {
      // Access token expired — check refresh token
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        const refreshPayload = JSON.parse(atob(refreshToken.split(".")[1]));
        if (refreshPayload.exp * 1000 < Date.now()) {
          // Both tokens expired — clear everything
          localStorage.removeItem("authToken");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("isAuthenticated");
        }
        // If refresh token is still valid, the 401 interceptor will handle it
      } else {
        localStorage.removeItem("authToken");
        localStorage.removeItem("isAuthenticated");
      }
    }
  } catch (e) {
    // Malformed token — clear it
    localStorage.removeItem("authToken");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("isAuthenticated");
  }
};
checkTokenExpiry();

function MainApp() {
  return (
    <Provider store={store}>
      <Router>
        <AuthHandler>
          <Routes>
            <Route path="/" element={<RedirectToLogin />} />
            <Route path="/login" element={<RedirectToLogin />} />
            <Route path="/itux-happiness-survey" element={<App />} />
            <Route path="/it-ux-survey" element={<App />} />
            <Route
              path="/lang"
              element={
                <ProtectedRoute>
                  <Lang />
                </ProtectedRoute>
              }
            />
            <Route
              path="/question"
              element={
                <ProtectedRoute>
                  <Question />
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedback"
              element={
                <ProtectedRoute>
                  <Feedback />
                </ProtectedRoute>
              }
            />
            <Route path="/it-ux-survey/itcenter" element={<HandleOldRoutes />} />
            <Route path="/error-page" element={<ErrorPage />} />
            {/* Catch-all route: undefined paths */}
            <Route path="*" element={<NotFoundRedirect />} />
          </Routes>
        </AuthHandler>
      </Router>
    </Provider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <MainApp />
  </React.StrictMode>
);
